import type { OverviewAgent } from '@/api/types';
import { formatCount, formatDateTime, formatPercent } from '@/lib/format';
import { SortableTh, Table, Td } from '@/ui/Table';
import { useSort, type SortColumns } from '@/ui/use-sort';

type AgentSortKey = 'agent' | 'links' | 'invited' | 'joined' | 'joinRate' | 'lastActivity';

const SORT_COLUMNS: SortColumns<OverviewAgent, AgentSortKey> = {
  agent: (agent) => agent.agentId,
  links: (agent) => agent.linksIssued,
  invited: (agent) => agent.playersInvited,
  joined: (agent) => agent.playersRedeemed,
  joinRate: (agent) => agent.redeemRate,
  lastActivity: (agent) => agent.lastEventAt,
};

export function AgentsTable({ agents }: { agents: OverviewAgent[] }) {
  const { sorted: rows, sort, toggle } = useSort(agents, SORT_COLUMNS, { key: 'joined', direction: 'desc' });

  return (
    <Table>
      <thead>
        <tr>
          <SortableTh align="left" sortKey="agent" sort={sort} onSort={toggle} first="asc">
            Agent
          </SortableTh>
          <SortableTh sortKey="links" sort={sort} onSort={toggle}>
            Links
          </SortableTh>
          <SortableTh sortKey="invited" sort={sort} onSort={toggle}>
            Invited
          </SortableTh>
          <SortableTh sortKey="joined" sort={sort} onSort={toggle}>
            Joined
          </SortableTh>
          <SortableTh sortKey="joinRate" sort={sort} onSort={toggle}>
            Join rate
          </SortableTh>
          <SortableTh align="left" sortKey="lastActivity" sort={sort} onSort={toggle}>
            Last activity
          </SortableTh>
        </tr>
      </thead>
      <tbody>
        {rows.map((agent) => (
          <tr key={agent.agentId}>
            <Td align="left">{agent.agentId}</Td>
            <Td>{formatCount(agent.linksIssued)}</Td>
            <Td>{formatCount(agent.playersInvited)}</Td>
            <Td>{formatCount(agent.playersRedeemed)}</Td>
            <Td>{formatPercent(agent.redeemRate)}</Td>
            <Td align="left">{formatDateTime(agent.lastEventAt)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
